import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
    Images,
    Calendar,
    Stethoscope,
    Activity,
    Flame,
    FlaskConical,
    Syringe,
    X,
    ChevronLeft,
    ChevronRight,
    ArrowRight,
    GitCompare,
    Loader2,
    Filter,
    Sparkles,
    AlertCircle,
    Tag as TagIcon,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
    appointment:   { label: 'Appointment',   icon: Stethoscope,  color: 'bg-sky-500',     text: 'text-sky-500',     route: '/appointments' },
    symptom:       { label: 'Symptom',       icon: Activity,     color: 'bg-rose-500',    text: 'text-rose-500',    route: '/symptoms' },
    flare_journal: { label: 'Flare',         icon: Flame,        color: 'bg-orange-500',  text: 'text-orange-500',  route: '/flare-journal' },
    lab_result:    { label: 'Lab',           icon: FlaskConical, color: 'bg-emerald-500', text: 'text-emerald-500', route: '/labs' },
    vaccine:       { label: 'Vaccine',       icon: Syringe,      color: 'bg-teal-500',    text: 'text-teal-500',    route: '/vaccines' },
};

const FILTER_PILLS = [
    { value: null, label: 'All' },
    { value: 'appointment', label: 'Appointments' },
    { value: 'symptom', label: 'Symptoms' },
    { value: 'flare_journal', label: 'Flares' },
    { value: 'lab_result', label: 'Labs' },
    { value: 'vaccine', label: 'Vaccines' },
];

export default function MyPhotos() {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState(null);
    const [filterTag, setFilterTag] = useState(null);
    const [availableTags, setAvailableTags] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [blobUrls, setBlobUrls] = useState({});
    const [lightboxIndex, setLightboxIndex] = useState(null);

    // Compare mode
    const [compareMode, setCompareMode] = useState(false);
    const [compareIds, setCompareIds] = useState([]);
    const [aiAnalyzing, setAiAnalyzing] = useState(false);
    const [aiResult, setAiResult] = useState(null);

    // ---------- Fetch photos ----------
    const fetchPhotos = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterType) params.parent_type = filterType;
            if (filterTag) params.tag = filterTag;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate + 'T23:59:59';
            const { data } = await axios.get(`${API_URL}/api/attachments/my-photos`, { params });
            setPhotos(data || []);
        } catch (e) {
            console.error('Failed to fetch photos', e);
            toast.error('Failed to load photos');
        } finally {
            setLoading(false);
        }
    }, [filterType, filterTag, startDate, endDate]);

    useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

    // Tag list
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/attachments/tags/list`);
                setAvailableTags(data.tags || []);
            } catch (e) { /* silent */ }
        })();
    }, [photos.length]);

    // ---------- Load blob URLs ----------
    useEffect(() => {
        if (photos.length === 0) return;
        let cancelled = false;
        const newUrls = { ...blobUrls };
        (async () => {
            for (const p of photos) {
                if (newUrls[p.id]) continue;
                try {
                    const resp = await axios.get(`${API_URL}/api/attachments/${p.id}/download`, { responseType: 'blob' });
                    if (cancelled) return;
                    newUrls[p.id] = URL.createObjectURL(resp.data);
                    setBlobUrls(u => ({ ...u, [p.id]: newUrls[p.id] }));
                } catch (e) { /* ignore */ }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photos]);

    // Revoke on unmount
    useEffect(() => () => {
        Object.values(blobUrls).forEach(u => URL.revokeObjectURL(u));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Lightbox keyboard nav
    useEffect(() => {
        if (lightboxIndex === null) return;
        const handler = (e) => {
            if (e.key === 'Escape') setLightboxIndex(null);
            if (e.key === 'ArrowLeft') setLightboxIndex(i => Math.max(0, i - 1));
            if (e.key === 'ArrowRight') setLightboxIndex(i => Math.min(photos.length - 1, i + 1));
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [lightboxIndex, photos.length]);

    // ---------- Group by date for chronological display ----------
    const grouped = useMemo(() => {
        const out = {};
        for (const p of photos) {
            const day = (p.created_at || '').slice(0, 10);
            if (!out[day]) out[day] = [];
            out[day].push(p);
        }
        return Object.entries(out).sort(([a], [b]) => b.localeCompare(a));
    }, [photos]);

    // ---------- Compare logic ----------
    const toggleCompareSelect = (id) => {
        setCompareIds(prev => {
            if (prev.includes(id)) return prev.filter(x => x !== id);
            if (prev.length >= 2) {
                toast.warning('Only two photos can be compared at once');
                return prev;
            }
            return [...prev, id];
        });
    };

    const exitCompare = () => {
        setCompareMode(false);
        setCompareIds([]);
        setAiResult(null);
    };

    const runAiCompare = async () => {
        if (compareIds.length !== 2) return;
        setAiAnalyzing(true);
        setAiResult(null);
        try {
            const { data } = await axios.post(`${API_URL}/api/attachments/ai-compare`, {
                attachment_a_id: compareIds[0],
                attachment_b_id: compareIds[1],
            });
            setAiResult(data);
        } catch (e) {
            const detail = e?.response?.data?.detail || 'AI analysis failed';
            toast.error(detail);
        } finally {
            setAiAnalyzing(false);
        }
    };

    // Reset AI result whenever the selected pair changes
    useEffect(() => {
        setAiResult(null);
    }, [compareIds]);

    const comparePhotos = compareIds.map(id => photos.find(p => p.id === id)).filter(Boolean);

    // ---------- Click handler ----------
    const onPhotoClick = (idx, photo) => {
        if (compareMode) {
            toggleCompareSelect(photo.id);
        } else {
            setLightboxIndex(idx);
        }
    };

    const activePhoto = lightboxIndex !== null ? photos[lightboxIndex] : null;
    const activeMeta = activePhoto ? TYPE_META[activePhoto.parent_type] : null;

    return (
        <div className="space-y-6" data-testid="my-photos-page">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Images className="w-7 h-7 text-indigo-500" />
                        My Photos
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        All your medical photos & attachments in one place
                    </p>
                </div>
                <div className="flex gap-2">
                    {compareMode ? (
                        <>
                            <Badge variant="outline" className="px-3 py-1.5">
                                {compareIds.length}/2 selected
                            </Badge>
                            <Button variant="outline" onClick={exitCompare} data-testid="compare-cancel">
                                <X className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="outline"
                            onClick={() => setCompareMode(true)}
                            disabled={photos.length < 2}
                            data-testid="compare-mode-btn"
                        >
                            <GitCompare className="w-4 h-4 mr-1" /> Compare
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Pills */}
            <Card>
                <CardContent className="py-4 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Filter className="w-4 h-4 text-muted-foreground" />
                        {FILTER_PILLS.map(p => (
                            <button
                                key={p.label}
                                type="button"
                                onClick={() => setFilterType(p.value)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    filterType === p.value
                                        ? 'bg-indigo-500 text-white'
                                        : 'bg-muted text-muted-foreground hover:bg-muted/70'
                                }`}
                                data-testid={`filter-pill-${p.value || 'all'}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-auto"
                            data-testid="start-date-input"
                        />
                        <span className="text-muted-foreground">to</span>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-auto"
                            data-testid="end-date-input"
                        />
                        {(startDate || endDate) && (
                            <Button variant="ghost" size="sm" onClick={() => { setStartDate(''); setEndDate(''); }}>
                                Clear
                            </Button>
                        )}
                    </div>

                    {/* Tag filter */}
                    {availableTags.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 text-sm pt-2 border-t border-slate-200 dark:border-slate-800">
                            <TagIcon className="w-4 h-4 text-muted-foreground" />
                            <button
                                type="button"
                                onClick={() => setFilterTag(null)}
                                className={`px-2.5 py-0.5 rounded-full text-xs ${filterTag === null ? 'bg-violet-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                                data-testid="tag-filter-all"
                            >
                                All tags
                            </button>
                            {availableTags.slice(0, 12).map(t => (
                                <button
                                    key={t.tag}
                                    type="button"
                                    onClick={() => setFilterTag(t.tag)}
                                    className={`px-2.5 py-0.5 rounded-full text-xs ${filterTag === t.tag ? 'bg-violet-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                                    data-testid={`tag-filter-${t.tag}`}
                                >
                                    {t.tag} <span className="opacity-60">·{t.count}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Compare strip */}
            {compareMode && comparePhotos.length === 2 && (
                <Card className="border-indigo-300 dark:border-indigo-700" data-testid="compare-view">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <GitCompare className="w-5 h-5" /> Side-by-side comparison
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {comparePhotos.map(p => {
                                const meta = TYPE_META[p.parent_type];
                                const Icon = meta?.icon;
                                return (
                                    <div key={p.id} className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Badge className={`${meta?.color} text-white`}>
                                                {Icon && <Icon className="w-3 h-3 mr-1" />}
                                                {meta?.label}
                                            </Badge>
                                            <span className="text-xs text-muted-foreground">
                                                {format(parseISO(p.created_at), 'MMM d, yyyy')}
                                            </span>
                                        </div>
                                        <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                                            {blobUrls[p.id] ? (
                                                <img src={blobUrls[p.id]} alt="" className="w-full h-full object-contain" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground truncate">{p.original_filename}</p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* AI Analysis */}
                        <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-800">
                            {!aiResult && (
                                <div className="flex flex-col items-start gap-2">
                                    <Button
                                        onClick={runAiCompare}
                                        disabled={aiAnalyzing}
                                        className="bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 text-white"
                                        data-testid="ai-compare-btn"
                                    >
                                        {aiAnalyzing ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</>
                                        ) : (
                                            <><Sparkles className="w-4 h-4 mr-2" /> Get AI analysis — What changed?</>
                                        )}
                                    </Button>
                                    <p className="text-xs text-muted-foreground">
                                        Uses GPT-5.2 vision to describe visible differences in plain English. Premium feature. Not medical advice.
                                    </p>
                                </div>
                            )}
                            {aiResult && (
                                <div className="space-y-3" data-testid="ai-compare-result">
                                    <div className="flex items-start gap-2">
                                        <Sparkles className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-violet-700 dark:text-violet-300 mb-1">
                                                AI Observation
                                            </p>
                                            <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200" data-testid="ai-observation-text">
                                                {aiResult.observation}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-xs flex gap-2">
                                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <p className="text-amber-900 dark:text-amber-200">{aiResult.disclaimer}</p>
                                    </div>
                                    <Button variant="outline" size="sm" onClick={runAiCompare} disabled={aiAnalyzing} data-testid="ai-compare-regenerate">
                                        {aiAnalyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                                        Regenerate
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Gallery */}
            <Card>
                <CardContent className="py-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : photos.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Images className="w-12 h-12 mx-auto mb-3 opacity-40" />
                            <p className="font-medium">No photos yet</p>
                            <p className="text-sm">Photos you attach to appointments, symptoms, flares, labs, or vaccines will appear here.</p>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {grouped.map(([day, items]) => (
                                <div key={day}>
                                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 sticky top-0 bg-background/95 py-1">
                                        {format(parseISO(day), 'EEEE, MMMM d, yyyy')}
                                        <span className="ml-2 text-xs font-normal">({items.length} {items.length === 1 ? 'photo' : 'photos'})</span>
                                    </h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                        {items.map((p) => {
                                            const meta = TYPE_META[p.parent_type] || {};
                                            const Icon = meta.icon;
                                            const idx = photos.findIndex(x => x.id === p.id);
                                            const isSelected = compareIds.includes(p.id);
                                            return (
                                                <div
                                                    key={p.id}
                                                    className={`relative group aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer transition-all ${
                                                        isSelected ? 'ring-4 ring-indigo-500' : 'hover:ring-2 hover:ring-indigo-300'
                                                    }`}
                                                    onClick={() => onPhotoClick(idx, p)}
                                                    data-testid={`my-photo-${p.id}`}
                                                >
                                                    {blobUrls[p.id] ? (
                                                        <img src={blobUrls[p.id]} alt={p.original_filename} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                                        </div>
                                                    )}
                                                    {/* Type badge */}
                                                    <div className={`absolute top-1.5 left-1.5 ${meta.color} text-white text-[10px] font-medium px-1.5 py-0.5 rounded flex items-center gap-1`}>
                                                        {Icon && <Icon className="w-3 h-3" />}
                                                        {meta.label}
                                                    </div>
                                                    {/* Compare check */}
                                                    {compareMode && (
                                                        <div className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                                                            isSelected
                                                                ? 'bg-indigo-500 border-indigo-500 text-white'
                                                                : 'bg-black/40 border-white text-white'
                                                        }`}>
                                                            {isSelected ? '✓' : ''}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Lightbox */}
            {activePhoto && (
                <div
                    className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
                    onClick={() => setLightboxIndex(null)}
                    data-testid="my-photos-lightbox"
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
                        className="absolute top-4 right-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
                        aria-label="Close"
                    >
                        <X className="w-6 h-6" />
                    </button>
                    {lightboxIndex > 0 && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i - 1); }}
                            className="absolute left-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
                            aria-label="Previous"
                        >
                            <ChevronLeft className="w-7 h-7" />
                        </button>
                    )}
                    {lightboxIndex < photos.length - 1 && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i + 1); }}
                            className="absolute right-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
                            aria-label="Next"
                        >
                            <ChevronRight className="w-7 h-7" />
                        </button>
                    )}
                    <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-3 max-w-full max-h-full">
                        <img
                            src={blobUrls[activePhoto.id]}
                            alt={activePhoto.original_filename}
                            className="max-w-[90vw] max-h-[75vh] object-contain rounded-lg"
                        />
                        <div className="flex items-center gap-3 bg-black/60 px-4 py-2 rounded-full">
                            <Badge className={`${activeMeta?.color} text-white`}>
                                {activeMeta?.label}
                            </Badge>
                            <span className="text-white text-sm">
                                {format(parseISO(activePhoto.created_at), 'MMM d, yyyy h:mm a')}
                            </span>
                            <span className="text-white/70 text-xs">
                                {lightboxIndex + 1} / {photos.length}
                            </span>
                            {activeMeta?.route && (
                                <Link
                                    to={activeMeta.route}
                                    className="ml-2 inline-flex items-center gap-1 text-indigo-300 hover:text-white text-sm"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid="goto-record-link"
                                >
                                    Go to record <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
